import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeliverablesSection = () => {
  const [activeReport, setActiveReport] = useState('comprehensive');

  const reportTypes = [
    {
      id: 'comprehensive',
      name: 'Detailed Report',
      description: 'Full-scope valuation with detailed analysis and supporting documentation',
      pages: '50-80 pages',
      timeline: '3-4 weeks',
      price: '$9,000 - $25,000',
      features: [
        'Executive summary with key findings',
        'Detailed company and industry analysis',
        'Complete methodology explanation',
        'Multiple valuation approaches',
        'Sensitivity and scenario analysis',
        'Appendices with supporting data',
        'Professional presentation format',
        'Court-admissible documentation'
      ],
      bestFor: 'Litigation, major transactions, regulatory compliance'
    },
    {
      id: 'summary',
      name: 'Summary Report',
      description: 'Focused valuation with key findings and limited detail',
      pages: '15-25 pages',
      timeline: '2-3 weeks',
      price: '$5,000 - $15,000',
      features: [
        'Executive summary',
        'Key valuation conclusions',
        'Primary methodology used',
        'Limited supporting analysis',
        'Basic sensitivity analysis',
        'Professional formatting',
        'Management presentation ready'
      ],
      bestFor: 'Internal planning, board presentations, investor discussions'
    },
    {
      id: 'calculation',
      name: 'Calculation Report',
      description: 'Basic valuation calculation with minimal analysis',
      pages: '5-15 pages',
      timeline: '1-2 weeks',
      price: '$3,000 - $8,000',
      features: [
        'Valuation conclusion',
        'Basic methodology outline',
        'Key assumptions listed',
        'Limited market analysis',
        'Simple presentation format'
      ],
      bestFor: 'Preliminary assessments, budget planning, initial discussions'
    }
  ];

  const sampleSections = [
    {
      title: 'Executive Summary',
      description: 'Key findings, valuation conclusion, and critical assumptions',
      icon: 'FileText',
      pages: '2-3 pages'
    },
    {
      title: 'Company Overview',
      description: 'Business description, history, operations, and market position',
      icon: 'Building',
      pages: '5-8 pages'
    },
    {
      title: 'Industry Analysis',
      description: 'Market conditions, competitive landscape, and growth prospects',
      icon: 'TrendingUp',
      pages: '8-12 pages'
    },
    {
      title: 'Financial Analysis',
      description: 'Historical performance, normalization adjustments, and projections',
      icon: 'BarChart3',
      pages: '10-15 pages'
    },
    {
      title: 'Valuation Analysis',
      description: 'Methodology application, calculations, and reconciliation',
      icon: 'Calculator',
      pages: '15-20 pages'
    },
    {
      title: 'Conclusion',
      description: 'Final value opinion, limiting conditions, and certifications',
      icon: 'CheckCircle',
      pages: '3-5 pages'
    }
  ];

  const additionalServices = [
    {
      title: 'Management Presentation',
      description: 'Executive-level presentation of key findings and recommendations',
      icon: 'Presentation',
      duration: '1-2 hours',
      included: true
    },
    {
      title: 'Expert Testimony',
      description: 'Court appearance and testimony support for litigation matters',
      icon: 'Scale',
      duration: 'As required',
      included: false
    },
    {
      title: 'Valuation Updates',
      description: 'Periodic updates for changing market conditions or business performance',
      icon: 'RefreshCw',
      duration: '1-2 weeks',
      included: false
    },
    {
      title: 'Due Diligence Support',
      description: 'Additional analysis and documentation for transaction support',
      icon: 'Search',
      duration: 'Variable',
      included: false
    }
  ];

  return (
    <section className="py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6">
            Deliverables & Reports
          </h2>
          <p className="text-xl text-text-secondary leading-relaxed">
            Professional valuation reports tailored to your specific needs, from comprehensive litigation support to summary reports for internal planning.
          </p>
        </div>

        {/* Report Types */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-primary mb-8 text-center">Report Types</h3>

          {/* Report Selection */}
          <div className="flex flex-col lg:flex-row gap-4 mb-8">
            {reportTypes?.map((report) => (
              <button
                key={report?.id}
                onClick={() => setActiveReport(report?.id)}
                className={`flex-1 p-6 rounded-xl border-2 transition-all duration-300 text-left ${activeReport === report?.id
                  ? 'border-accent bg-white shadow-brand'
                  : 'border-border bg-white hover:border-accent/50'
                  }`}
              >
                <h4 className="text-lg font-semibold text-primary mb-2">{report?.name}</h4>
                <p className="text-sm text-text-secondary mb-4">{report?.description}</p>

                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-text-secondary">Pages</p>
                    <p className="font-medium text-primary">{report?.pages}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-secondary">Timeline</p>
                    <p className="font-medium text-primary">{report?.timeline}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-secondary">Investment</p>
                    <p className="font-medium text-accent">{report?.price}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Active Report Details */}
          <div className="bg-white rounded-2xl p-8 border border-border">
            {reportTypes?.map((report) => (
              activeReport === report?.id && (
                <div key={report?.id} className="grid lg:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-xl font-semibold text-primary mb-6">What's Included</h4>
                    <ul className="space-y-3">
                      {report?.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                          <span className="text-text-primary">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-muted p-6 rounded-xl">
                      <h5 className="font-semibold text-primary mb-3">Best Suited For</h5>
                      <p className="text-text-secondary">{report?.bestFor}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-accent/5 rounded-lg">
                        <Icon name="Clock" size={24} className="text-accent mx-auto mb-2" />
                        <p className="text-sm text-text-secondary">Delivery</p>
                        <p className="font-semibold text-primary">{report?.timeline}</p>
                      </div>
                      <div className="text-center p-4 bg-success/5 rounded-lg">
                        <Icon name="DollarSign" size={24} className="text-success mx-auto mb-2" />
                        <p className="text-sm text-text-secondary">Investment</p>
                        <p className="font-semibold text-primary">{report?.price}</p>
                      </div>
                    </div>

                    {/* <Button variant="default" fullWidth iconName="Download" iconPosition="left">
                      Download Sample Report
                    </Button> */}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>

        {/* Report Structure */}
        {/* <div className="mb-16">
          <h3 className="text-2xl font-bold text-primary mb-8 text-center">Comprehensive Report Structure</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sampleSections?.map((section, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-border hover:shadow-brand transition-shadow duration-300">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name={section?.icon} size={20} className="text-accent" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-primary">{section?.title}</h4>
                    <p className="text-sm text-accent">{section?.pages}</p>
                  </div>
                </div>
                <p className="text-sm text-text-secondary">{section?.description}</p>
              </div>
            ))}
          </div>
        </div> */}

        {/* Additional Services */}
        <div>
          <h3 className="text-2xl font-bold text-primary mb-8 text-center">Additional Services</h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices?.map((service, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-border text-center hover:shadow-brand transition-shadow duration-300">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name={service?.icon} size={24} className="text-accent" />
                </div>

                <h4 className="text-lg font-semibold text-primary mb-2">{service?.title}</h4>
                <p className="text-sm text-text-secondary mb-4">{service?.description}</p>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">{service?.duration}</span>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${service?.included
                    ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                    }`}>
                    {service?.included ? 'Included' : 'Add-on'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeliverablesSection;